import React from 'react'
import { useTranslations } from '~/components/Locale'
import type { HTMLElementProps, ProductOption, Variant } from '~/types'
import { cn, getCurrentCountry, getPrice, getShadowColor, imageUrl } from '~/utils'

export interface VariantOptionProps extends HTMLElementProps<'input'> {
	variant: Variant
	option: ProductOption
	prefer?: 'color' | 'image'
	price: number
	badge?: string
	onChange: () => void
}

export const VariantOption: React.FC<VariantOptionProps> = ({
	badge,
	prefer = 'image',
	option,
	price,
	variant,
	children,
	...props
}) => {
	const t = useTranslations('react.presale')
	const color = prefer === 'color' ? (variant.option_color?.value ?? option.swatch?.color) : null
	const imageRef = variant.option_image?.reference?.image ?? option.swatch?.image
	const image = prefer === 'image' && imageRef ? 'url' in imageRef && imageRef.url : null
	return (
		<label
			className={cn(
				'relative flex cursor-pointer items-center justify-between',
				'rounded-2xl ring-2',
				props.disabled ? 'cursor-not-allowed opacity-60' : 'group hover:ring-green-200',
				props.checked ? 'bg-green/15 ring-green-500' : 'bg-stone-100 ring-transparent',
				image ? 'pl-6' : 'px-6 py-4'
			)}
		>
			<input className="sr-only" type="radio" {...props} />
			<span className="font-semibold">{children}</span>
			{window.PRODUCT?.handle !== 'gift-card' && (
				<span className="ml-3 flex-auto text-lg font-bold text-gray-600">
					{price > 0 && variant.availableForSale ? (
						<span>
							+&nbsp;
							{getCurrentCountry().currency.symbol.length === 1 ? getPrice(price, true, 0) : price}
						</span>
					) : variant.availableForSale ? null : (
						<span>{t?.sold_out}</span>
					)}
				</span>
			)}
			{image ? (
				<img className="h-16" src={imageUrl(image, { height: 100 })} />
			) : color ? (
				<span className={cn('rounded-full p-5', getShadowColor(color))} style={{ background: color }} />
			) : (
				<div
					className={cn(
						'rounded-full p-1 outline outline-2',
						props.checked ? 'outline-green-500' : !props.disabled ? 'outline-green-700' : 'outline-gray-400'
					)}
				>
					<div
						className={cn(
							'rounded-full p-2',
							props.checked ? 'bg-green-500' : 'bg-transparent group-hover:bg-green-200'
						)}
					/>
				</div>
			)}
			{badge && (
				<aside
					className={
						'absolute -top-4 right-16 rounded-full px-4 py-0.5 ' +
						'text-10 bg-green-200 font-semibold text-green-800'
					}
				>
					{badge?.toUpperCase()}
				</aside>
			)}
		</label>
	)
}
